import * as THREE from 'three';
import { rideDef } from '../content/rides';
import { needItem, needMoney, type Choice } from '../content/types';
import { RemoteAvatar } from '../entities/RemoteAvatar';
import {
  formatParkCode,
  generateParkCode,
  inviteUrl,
  normalizeParkCode,
  RateLimiter,
  type FxMsg,
  type SocialKind,
} from '../net/protocol';
import {
  Session,
  type Neighbor,
  type Profile,
  type SessionStatus,
  type TransportFactory,
} from '../net/Session';
import { trysteroTransport } from '../net/trystero';
import { homeLot, lotLabel } from '../world/home';
import type { Outcome } from '../state/Store';
import { isWoman } from '../content/looks';
import { rankFor } from '../state/rules';
import type { CharacterLook } from '../state/types';
import type { Interactions } from '../systems/Interactions';
import type { OnlineHost, OnlineView } from '../ui/OnlinePanel';
import type { StageHost } from '../ui/StageHost';
import type { Activities } from './Activities';
import { Duels } from './Duels';
import type { VisitableHome } from './Interiors';
import type { Driving } from './Driving';
import { NeighborHomes } from './NeighborHomes';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';

const LAST_PARK_KEY = 'double-wide-dreams/park/v1';
/** The park you opened yourself (you keep Lot 7 there). */
const HOSTED_KEY = 'double-wide-dreams/hosted/v1';
const CHAT_LOG_MAX = 40;
/** Name tags fade out past this distance. */
const TAG_RANGE = 60;
const SELF_BUBBLE = 'self';

export interface NeighborsDeps {
  interactions: Interactions;
  activities: Activities;
  driving: Driving;
  rest: RestCycle;
  portraits: Map<string, string>;
  renderPortrait(look: CharacterLook): string;
  /** True while a dialog, shop or the phone covers the game. */
  inMenu(): boolean;
  /** Which home lot you live on (index into HOME_LOTS). */
  homeIndex(): number;
  /** Move your trailer, yard and Camaro to another lot. */
  moveHome(index: number): void;
  /** 0..1 how dark it is (yard lights, bug zappers). */
  night(): number;
  /** Hosts the live part of arm wrestling. */
  stages: StageHost;
  /** The park buyback jar, pooled. */
  fund(): { total: number; goal: number };
  /** Your light's on (so others see it). */
  lampOn(): boolean;
  /** Somebody else is getting beamed up: show the saucer over them. */
  ufo(x: number, z: number): void;
}

const HANGOUT_LINES: Array<(name: string) => string> = [
  (n) => `${n} is leaning on the chain-link fence like they own it. They might, actually.`,
  (n) => `${n} nods at you. You nod back. A whole conversation just happened.`,
  (n) => `"Hot enough for ya?" says ${n}. It is always hot enough for ya.`,
  (n) => `${n} is telling you about a guy who knows a guy who can get Camaro parts cheap.`,
  (n) => `${n} smells like sunscreen, gasoline and ambition.`,
];

const ROASTS: Array<(name: string) => string> = [
  (n) => `Nice jorts, ${n}. Did your mama cut those or a raccoon?`,
  () => "If brains were bourbon, you couldn't get a squirrel tipsy.",
  () => "That Camaro's been on blocks so long it's a historical landmark.",
  () => "You call that a beer belly? That's a beer pothole.",
  (n) => `Bless your heart, ${n}. And bless them Crocs.`,
  () => "You're about as useful as a screen door on a submarine.",
  () => "I've seen better yards at the county dump.",
  (n) => `${n}, your mullet's got more business up front than you do.`,
];

function readInvite(): string | null {
  const url = new URL(window.location.href);
  const code = normalizeParkCode(url.searchParams.get('park'));
  if (url.searchParams.has('park')) {
    // Tidy the address bar so a later copy-paste of the URL doesn't re-share the park.
    url.searchParams.delete('park');
    window.history.replaceState(window.history.state, '', url);
  }
  return code;
}

function readLastPark(): string | null {
  try {
    return normalizeParkCode(localStorage.getItem(LAST_PARK_KEY));
  } catch {
    return null;
  }
}

function storeLastPark(code: string): void {
  try {
    localStorage.setItem(LAST_PARK_KEY, code);
  } catch {
    // Private mode or storage full: "rejoin" just won't be offered.
  }
}

function readHosted(): string | null {
  try {
    return normalizeParkCode(localStorage.getItem(HOSTED_KEY));
  } catch {
    return null;
  }
}

function storeHosted(code: string): void {
  try {
    localStorage.setItem(HOSTED_KEY, code);
  } catch {
    // Without storage you just won't get Lot 7 back if you rejoin your own park.
  }
}

/**
 * "Neighbors Online": drop-in multiplayer. Other players walk around your Lucky Acres
 * while each of you keeps your own money, rent and clock. This module connects the
 * network session to the game: avatars, name tags, chat bubbles, hanging out
 * (clinking cans, tossing a beer) and sharing big moments like fireworks and tornadoes.
 */
export class Neighbors implements OnlineHost {
  readonly session: Session;
  /** A park to join when gameplay starts: from an invite link, or the one we were in. */
  pending: { code: string; invited: boolean } | null;
  private readonly avatars = new Map<string, RemoteAvatar>();
  private readonly chatLog: Array<{ name: string; text: string; self: boolean }> = [];
  private readonly listeners = new Set<() => void>();
  private readonly rewards = new RateLimiter();
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();
  private readonly homes: NeighborHomes;
  private joinedAt = 0;
  /** We opened this park. */
  private hosting = false;
  private homeSig = '';
  private sigTimer = 0;
  readonly duels: Duels;

  constructor(
    private readonly s: GameServices,
    private readonly deps: NeighborsDeps,
    transport: TransportFactory = trysteroTransport,
  ) {
    this.session = new Session({ connect: transport, profile: () => this.profile() });
    this.homes = new NeighborHomes(s.world, {
      myClaim: () => ({ id: 'me', lot: deps.homeIndex(), host: this.hosting, since: this.session.since }),
      moveMyHome: (index) => this.moveMyHome(index),
    });
    const bus = this.session.bus;
    bus.on('status', (status) => this.onStatus(status));
    bus.on('join', (n) => this.onJoin(n));
    bus.on('leave', (n) => this.onLeave(n));
    bus.on('update', (n) => {
      this.restyle(n);
      this.homes.sync(this.session.neighbors);
      this.changed();
    });
    bus.on('state', ({ from, state }) => this.avatars.get(from.id)?.buffer.push(state, this.clock()));
    bus.on('chat', ({ from, text }) => this.onChat(from, text));
    bus.on('social', ({ from, kind }) => this.onSocial(from, kind));
    bus.on('fx', ({ from, fx }) => this.onFx(from, fx));
    this.duels = new Duels(s, deps.stages, {
      send: (peer, msg) => this.session.duel(peer, msg),
      neighbor: (id) => this.session.neighbor(id),
      avatar: (id) => this.avatars.get(id) ?? null,
      busy: () => deps.inMenu() || deps.rest.sleeping || deps.driving.active,
    });
    bus.on('duel', ({ from, msg }) => this.duels.onMessage(from, msg));
    bus.on('problem', (text) => s.store.toast(text, 'bad', ':satellite-dish:'));
    const invite = readInvite();
    this.pending = invite ? { code: invite, invited: true } : null;
  }

  get online(): boolean {
    return this.session.status !== 'offline';
  }

  /** Our game clock in ms: animation and network replay run on game time. */
  private clock(): number {
    return this.s.now() * 1000;
  }

  // --- lifecycle ---------------------------------------------------------------------

  /** Gameplay started (new game or continue): move into any park we were invited to. */
  enterPlay(): void {
    const park = this.pending;
    this.pending = null;
    if (park) this.join(park.code);
  }

  /** Back to the title screen: step out of the park, and come back when play resumes. */
  exitPlay(): void {
    const code = this.session.code;
    if (!code) return;
    this.pending = { code, invited: false };
    this.session.leave();
  }

  host(): void {
    const code = generateParkCode();
    storeHosted(code);
    this.join(code);
  }

  join(raw: string): boolean {
    const code = normalizeParkCode(raw);
    if (!code) return false;
    if (code === this.session.code) return true;
    this.hosting = readHosted() === code;
    void this.session.join(code);
    return true;
  }

  leave(): void {
    if (!this.online) return;
    this.session.leave();
    this.s.store.toast('You left the park. Back to single-wide, single-player life.', 'info', ':door:');
  }

  /** Broadcasts a big moment (fireworks, a tornado...) so neighbors see it too. */
  share(fx: FxMsg): void {
    this.session.fx(fx);
  }

  say(raw: string): boolean {
    const text = this.session.chat(raw);
    if (!text) return false;
    this.log(this.s.store.state.player.name, text, true);
    this.s.ui.hud.bubble(SELF_BUBBLE, text, this.bubbleAnchor(this.s.player.headPosition()), 5);
    return true;
  }

  /** Chat bubbles float above the name tag. */
  private bubbleAnchor(head: THREE.Vector3): THREE.Vector3 {
    return head.setY(head.y + 0.95);
  }

  // --- per frame ----------------------------------------------------------------------

  update(dt: number): void {
    if (!this.online) return;
    this.session.tick();
    this.duels.update(dt);
    this.sendState();
    this.sigTimer -= dt;
    if (this.sigTimer <= 0) {
      // Bought decor or fixed the Camaro? Tell the neighbors so your lot looks right.
      this.sigTimer = 1;
      const st = this.profile().status;
      const sig = `${st.lot}|${st.car}|${st.home}|${st.ride}|${st.decor.join()}|${st.inside.join()}|${st.walls}|${st.floor}|${st.fund}|${Object.values(st.rec).join()}`;
      if (sig !== this.homeSig) {
        this.homeSig = sig;
        this.session.sendStatusNow();
      }
    }
    this.homes.update(
      this.session.neighbors,
      (id) => !!this.avatars.get(id)?.state?.driving,
      this.s.now(),
      dt,
      this.deps.night(),
    );
    const { hud } = this.s.ui;
    const now = this.clock();
    const time = this.s.now();
    const me = this.s.player.position;
    for (const n of this.session.neighbors) {
      const avatar = this.avatars.get(n.id);
      if (!avatar) continue;
      if (n.status) avatar.setRide(n.status.ride);
      avatar.update(dt, time, now);
      const key = `peer:${n.id}`;
      const at = avatar.labelAnchor(this.tmp);
      const near = at !== null && Math.hypot(at.x - me.x, at.z - me.z) < TAG_RANGE;
      const doing = n.away ? ' :zzz:' : avatar.state?.menu ? ' :phone:' : '';
      hud.setTag(key, `${n.name}${doing}`, near ? this.tmp2.copy(at).setY(at.y + 0.35) : null);
      if (at) hud.moveBubble(key, this.bubbleAnchor(at));
    }
    hud.moveBubble(SELF_BUBBLE, this.bubbleAnchor(this.s.player.headPosition(this.tmp)));
  }

  private sendState(): void {
    const { player } = this.s;
    const { driving, rest, inMenu } = this.deps;
    const root = player.rig.root;
    const last = player.anim.last;
    this.session.sendState({
      x: root.position.x,
      y: root.position.y,
      z: root.position.z,
      facing: root.rotation.y,
      pose: last?.pose ?? 'stand',
      speed: last?.speed ?? 0,
      sprint: !!last?.sprinting,
      airborne: !!last?.airborne,
      seat: last?.seat ?? 0.45,
      // Only stunts drive the hip pivot directly; everything else is a pose.
      flip: player.scripted ? player.rig.body.rotation.x : 0,
      driving: driving.active,
      hidden: player.hidden,
      menu: inMenu(),
      asleep: rest.sleeping,
      lamp: this.deps.lampOn(),
      carX: driving.car.position.x,
      carZ: driving.car.position.z,
      carHeading: driving.car.heading,
      t: this.clock(),
    });
  }

  private profile(): Profile {
    const { store } = this.s;
    const rating = store.rating();
    const lot = this.deps.homeIndex();
    return {
      name: store.state.player.name,
      look: store.state.player.look,
      lot,
      host: this.hosting,
      status: {
        rating,
        rank: rankFor(rating, isWoman(store.state.player.look)).rank.title,
        day: store.day,
        lot,
        decor: [...store.state.decor],
        car: store.flag('camaro_sold') ? 2 : store.state.camaro >= 100 ? 1 : 0,
        home: store.flag('double_wide') || store.flag('won') ? 1 : 0,
        ride: rideDef(store.state.ride).id,
        rec: {
          bull: store.counter('bullBest'),
          mud: Math.round(Number(store.flag('mud_best') ?? 0) * 10),
          shoot: store.counter('shootScore'),
          catfish: store.counter('catfishBest'),
          duels: store.counter('duelWins'),
        },
        look: store.state.player.look,
        inside: store.displayedInterior(),
        walls: store.state.interior.walls,
        floor: store.state.interior.floor,
        fund: Math.round(store.state.fund),
      },
    };
  }

  /** Someone with dibs has your lot: your trailer gets hauled to a free one. */
  private moveMyHome(index: number): void {
    const from = this.deps.homeIndex();
    this.deps.moveHome(index);
    this.session.sendStatusNow();
    // And again shortly, in case the first one got squeezed by rate limiting.
    this.homeSig = '';
    this.sigTimer = Math.min(this.sigTimer, 0.35);
    const frame = homeLot(index);
    if (index === 0)
      this.s.store.toast('Your trailer is back on Lot 7, right where Uncle left it.', 'info', ':trailer:');
    else {
      const owner = this.session.neighbors.find((n) => n.lot === from);
      this.s.store.toast(
        `Darlene had your trailer hauled to ${lotLabel(frame)}.${owner ? ` ${owner.name} has Lot ${homeLot(from).number} in this park.` : ''}`,
        'info',
        ':semi:',
      );
    }
  }

  // --- session events -----------------------------------------------------------------

  private onStatus(status: SessionStatus): void {
    const code = this.session.code;
    if (status === 'open' && code) {
      this.joinedAt = performance.now();
      storeLastPark(code);
      this.s.store.toast(
        `You're in park ${formatParkCode(code)}. Invite folks from your phone.`,
        'good',
        ':camp:',
      );
    }
    if (status === 'offline') {
      this.duels.reset();
      this.chatLog.length = 0;
      this.homes.clear();
      this.homeSig = '';
      // Playing solo again: home is Lot 7.
      if (this.deps.homeIndex() !== 0) this.moveMyHome(0);
    }
    this.changed();
  }

  /** They changed clothes or got a haircut: rebuild their character. */
  private restyle(n: Neighbor): void {
    const look = n.status?.look;
    const avatar = this.avatars.get(n.id);
    if (!look || !avatar || JSON.stringify(look) === JSON.stringify(avatar.rig.look)) return;
    avatar.dispose();
    this.avatars.set(n.id, new RemoteAvatar(this.s.world.dynamic, look));
    this.deps.portraits.set(`peer:${n.id}`, this.deps.renderPortrait(look));
  }

  /** You changed your look: tell the park now rather than in a few seconds. */
  lookChanged(): void {
    if (this.online) this.session.sendStatusNow();
  }

  private onJoin(n: Neighbor): void {
    const key = `peer:${n.id}`;
    this.avatars.set(n.id, new RemoteAvatar(this.s.world.dynamic, n.look));
    this.deps.portraits.set(key, this.deps.renderPortrait(n.look));
    this.deps.interactions.add({
      id: key,
      at: () => {
        const st = this.avatars.get(n.id)?.state;
        return st ? { x: st.x, z: st.z } : { x: Infinity, z: Infinity };
      },
      radius: 2.2,
      prompt: () => (this.canHangOut(n) ? `Hang out with ${n.name}` : null),
      action: () => this.hangOut(n),
    });
    this.homes.sync(this.session.neighbors);
    this.s.store.toast(`${n.name} pulled into Lucky Acres.`, 'good', ':trailer:');
    this.s.sfx('horn');
    this.changed();
  }

  private onLeave(n: Neighbor): void {
    const key = `peer:${n.id}`;
    this.avatars.get(n.id)?.dispose();
    this.avatars.delete(n.id);
    this.deps.portraits.delete(key);
    this.deps.interactions.remove(key);
    this.s.ui.hud.removeTag(key);
    this.homes.sync(this.session.neighbors);
    // When we're the ones leaving, the session is already offline.
    if (this.session.status === 'open') this.s.store.toast(`${n.name} left the park.`, 'info', ':car:');
    this.changed();
  }

  private onChat(n: Neighbor, text: string): void {
    this.log(n.name, text, false);
    const at = this.avatars.get(n.id)?.labelAnchor(this.tmp);
    if (at) this.s.ui.hud.bubble(`peer:${n.id}`, text, this.bubbleAnchor(at), 5);
    this.s.store.toast(`${n.name}: ${text}`, 'info', ':speech:');
  }

  private onSocial(n: Neighbor, kind: SocialKind): void {
    const { store, ui } = this.s;
    const now = performance.now();
    const at = this.avatars.get(n.id)?.labelAnchor(this.tmp);
    switch (kind) {
      case 'clink':
        store.toast(`${n.name} clinked cans with you! Cheers, neighbor.`, 'good', ':cheers:');
        if (at) ui.hud.float(':cheers: CHEERS', at, '#ffd400');
        if (this.rewards.allow(`clinked:${n.id}`, now, 60_000)) store.addClout(1);
        this.s.sfx('canOpen');
        break;
      case 'gift':
        // A beer, at most every 20 seconds per neighbor: generous, not a beer printer.
        if (!this.rewards.allow(`gifted:${n.id}`, now, 20_000)) return;
        store.give('beer', 1);
        store.toast(`${n.name} tossed you a cold one! +1 :beer:`, 'good', ':gift:');
        this.s.sfx('pickup');
        break;
      case 'dap':
        store.toast(`${n.name} dapped you up.`, 'good', ':fist-bump:');
        if (this.rewards.allow(`dapped:${n.id}`, now, 300_000)) store.addClout(1);
        break;
    }
  }

  private onFx(n: Neighbor, fx: FxMsg): void {
    const { effects, store } = this.s;
    switch (fx.kind) {
      case 'fireworks':
        effects.launchFireworks(fx.x, fx.z, 6);
        break;
      case 'tornado':
        if (effects.tornadoActive) return;
        effects.startTornado();
        store.toast(`${n.name}'s got a tornado coming. Y'all might wanna get inside.`, 'bad', ':tornado:');
        break;
      case 'cops':
        effects.startCops();
        store.toast(
          `Cops are rolling into Lucky Acres. ${n.name} swears it wasn't them.`,
          'info',
          ':police-car:',
        );
        break;
      case 'stunt':
        if (fx.ok) store.toast(`${n.name} stuck the ${fx.name}! The park goes nuts.`, 'good', ':cartwheel:');
        else
          store.toast(
            `${n.name} ate it on the trampoline. Somebody get the frozen peas.`,
            'meme',
            ':ambulance:',
          );
        break;
      case 'ufo':
        this.deps.ufo(fx.x, fx.z);
        break;
    }
  }

  // --- hanging out --------------------------------------------------------------------

  private canHangOut(n: Neighbor): boolean {
    const st = this.avatars.get(n.id)?.state;
    return !!st && !st.driving && !st.hidden && !st.asleep && !this.deps.driving.active;
  }

  private hangOut(n: Neighbor): void {
    const { player, rng, ui } = this.s;
    const st = this.avatars.get(n.id)?.state;
    if (st) {
      player.facing = Math.atan2(st.x - player.position.x, st.z - player.position.z);
      player.syncTransform();
    }
    const beer = needItem('beer', 1, 'beer');
    const choices: Choice[] = [
      { label: ':cheers: Clink cans', hint: '-1 :beer:', requires: beer, run: () => this.clink(n) },
      { label: ":gift: Toss 'em a cold one", hint: '-1 :beer:', requires: beer, run: () => this.gift(n) },
      { label: ":fist-bump: Dap 'em up", run: () => this.dap(n) },
      { label: ':shout: Talk a little trash', run: () => this.roast(n) },
      ...[0, 10].map((bet): Choice => ({
        label: `:flex: Arm wrestle${bet ? ` for $${bet}` : ''}`,
        hint: bet ? 'winner takes $' + bet * 2 : 'for pride',
        requires: needMoney(bet),
        run: () => {
          void this.duels.invite(n, 'arm', bet);
        },
      })),
      ...[5, 25].map((bet): Choice => ({
        label: `:dice: Shoot dice for $${bet}`,
        hint: 'high roll wins',
        requires: needMoney(bet),
        run: () => {
          void this.duels.invite(n, 'dice', bet);
        },
      })),
    ];
    ui.modal.show({
      title: `Hangin' with ${n.name}`,
      speaker: {
        id: `peer:${n.id}`,
        name: n.status ? `${n.status.rank} · Day ${n.status.day}` : 'Your neighbor',
        voice: 250,
      },
      text: rng.pick(HANGOUT_LINES)(n.name),
      choices,
    });
  }

  private clink(n: Neighbor): Outcome {
    if (!this.deps.activities.drinkBeer()) return { text: 'You fumble the can. Try that again.' };
    this.session.social(n.id, 'clink');
    const text = `*CLINK* You and ${n.name} crack 'em open at the exact same time. Synchronized drinking. Beautiful.`;
    return this.rewards.allow(`clink:${n.id}`, performance.now(), 60_000) ? { text, clout: 2 } : { text };
  }

  private gift(n: Neighbor): Outcome {
    if (!this.s.store.take('beer')) return { text: "You're all out. Awkward." };
    this.session.social(n.id, 'gift');
    const text = `You toss ${n.name} a cold one. Perfect spiral. They'll remember this.`;
    return this.rewards.allow(`gift:${n.id}`, performance.now(), 60_000) ? { text, clout: 1 } : { text };
  }

  private dap(n: Neighbor): Outcome {
    this.session.social(n.id, 'dap');
    this.s.player.doBusy({ pose: 'wave', until: this.s.now() + 1.2 });
    const text = `You dap up ${n.name}. Firm. Respectful. Slightly sticky.`;
    return this.rewards.allow(`dap:${n.id}`, performance.now(), 300_000) ? { text, clout: 1 } : { text };
  }

  private roast(n: Neighbor): Outcome {
    const line = this.s.rng.pick(ROASTS)(n.name);
    this.say(line);
    return { text: `You lean on the fence and tell ${n.name}: "${line}"` };
  }

  // --- phone UI -------------------------------------------------------------------------

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** What each neighbor here right now has put into the park buyback fund. */
  fundContributions(): number[] {
    if (!this.online) return [];
    return this.session.neighbors.map((n) => n.status?.fund ?? 0);
  }

  /** Other players' homes you can walk into: placed on their lot, with their stuff inside. */
  visitableHomes(): VisitableHome[] {
    if (!this.online) return [];
    const out: VisitableHome[] = [];
    for (const p of this.homes.placedHomes()) {
      const n = this.session.neighbors.find((x) => x.id === p.id);
      if (!n) continue;
      const st = n.status;
      out.push({
        index: p.lot,
        name: n.name,
        kind: p.kind,
        walls: st?.walls ?? 'paneling',
        floor: st?.floor ?? 'shag',
        items: st?.inside ?? [],
      });
    }
    return out;
  }

  view(): OnlineView {
    const { store } = this.s;
    const session = this.session;
    const rating = store.rating();
    const me = this.s.player.position;
    return {
      status: session.status,
      code: session.code,
      lastCode: readLastPark(),
      invite: this.invite,
      waitingMs: session.status === 'open' ? performance.now() - this.joinedAt : 0,
      me: {
        name: store.state.player.name,
        portrait: this.deps.portraits.get('player'),
        detail: `Lot ${homeLot(this.deps.homeIndex()).number} · ${rankFor(rating).rank.title} · ${rating} pts`,
      },
      neighbors: session.neighbors.map((n) => {
        const st = this.avatars.get(n.id)?.state;
        const where = n.away
          ? ':zzz: Away'
          : st?.asleep
            ? ':zzz: Sleeping'
            : st?.driving
              ? ':car: Driving'
              : st?.menu
                ? ':phone: On their phone'
                : st
                  ? `${Math.round(Math.hypot(st.x - me.x, st.z - me.z))}m away`
                  : 'Pulling in…';
        return {
          id: n.id,
          name: n.name,
          portrait: this.deps.portraits.get(`peer:${n.id}`),
          detail: `Lot ${homeLot(n.lot).number}${n.status ? ` · ${n.status.rank} · ${n.status.rating} pts` : ''}`,
          where,
        };
      }),
      chat: this.chatLog,
      records: this.records(),
      fund: this.deps.fund(),
    };
  }

  /** The best anybody in the park has done (you included). */
  private records(): OnlineView['records'] {
    const { store } = this.s;
    const people = [
      {
        name: `${store.state.player.name} (you)`,
        rec: {
          bull: store.counter('bullBest'),
          mud: Math.round(Number(store.flag('mud_best') ?? 0) * 10),
          shoot: store.counter('shootScore'),
          catfish: store.counter('catfishBest'),
          duels: store.counter('duelWins'),
        },
      },
      ...this.session.neighbors.filter((n) => n.status).map((n) => ({ name: n.name, rec: n.status!.rec })),
    ];
    const out: Array<{ label: string; holder: string; value: string }> = [];
    const best = (
      label: string,
      key: keyof (typeof people)[number]['rec'],
      fmt: (v: number) => string,
      lower = false,
    ): void => {
      const valid = people.filter((p) => p.rec[key] > 0);
      if (!valid.length) return;
      const top = valid.reduce((a, b) =>
        (lower ? b.rec[key] < a.rec[key] : b.rec[key] > a.rec[key]) ? b : a,
      );
      out.push({ label, holder: top.name, value: fmt(top.rec[key]) });
    };
    best(':bull: Longest bull ride', 'bull', (v) => `${(v / 10).toFixed(1)}s`);
    best(':checkered-flag: Fastest mud run', 'mud', (v) => `${(v / 10).toFixed(1)}s`, true);
    best(':target: Best day at the range', 'shoot', (v) => `${v} pts`);
    best(':catfish: Biggest catfish', 'catfish', (v) => `${v} lb`);
    best(':flex: Challenges won', 'duels', (v) => String(v));
    return out;
  }

  private get invite(): string | null {
    const code = this.session.code;
    return code ? inviteUrl(window.location.href, code) : null;
  }

  async copyInvite(): Promise<boolean> {
    const url = this.invite;
    if (!url) return false;
    try {
      await navigator.clipboard.writeText(url);
      this.s.store.toast('Invite link copied. Paste it to your people.', 'good', ':clipboard:');
      return true;
    } catch {
      window.prompt('Copy this invite link:', url);
      return false;
    }
  }

  get canShare(): boolean {
    return typeof navigator.share === 'function';
  }

  shareInvite(): void {
    const { code } = this.session;
    const invite = this.invite;
    if (!code || !invite || !this.canShare) return;
    navigator
      .share({
        title: 'Double Wide Dreams',
        text: `Come be my neighbor in Lucky Acres! Park code ${formatParkCode(code)}`,
        url: invite,
      })
      .catch(() => undefined); // dismissed the share sheet
  }

  private log(name: string, text: string, self: boolean): void {
    this.chatLog.push({ name, text, self });
    if (this.chatLog.length > CHAT_LOG_MAX) this.chatLog.shift();
    this.changed();
  }

  private changed(): void {
    const count = this.online ? this.session.neighbors.length : null;
    this.s.ui.hud.setOnline(count);
    for (const fn of this.listeners) fn();
  }
}
